/**
 * SalonKartiPage.jsx — Salon Kartı: SEÇİLİ FUARDAKİ bir salonun tek sayfası (backlog #163, 2026-09-26)
 * Route: /Management/Salonlar/:salonId   (Salonlar listesindeki bir satırdan açılır, #157)
 *
 * Kapsam Bilge'nin kararı (2026-09-26): "tam salon kartı" — salon bilgileri (ad, brüt m², tavan,
 * izin verilen yükseklik), dört durumlu hizmetler (#194), üç planın çizim durumu (#202) ve
 * değiştirme kontrolleri, "Stand Çizimi Tamamlandı", stand sayısı, bu fuarda aktif/pasif, ve
 * plan sayfalarına + Yükseklik Limitleri'ne bağlantılar.
 *
 * Veri — tek bir birleşik uç yok; SalonlarPage ile aynı kaynaklar, bir salona süzülmüş:
 *   getFuarHMax(fuarId)           bu fuara BAĞLI salonlar: Alan Planı durumu, tek/çift kat limitleri
 *   getFuarAlani(fuarAlaniId)     fuar alanındaki salon: brüt m², tavan, hizmetler, Salon/Altyapı Planı
 *   getSalonlarByFuar(fuarId)     salon başına stand sayısı (krokiService)
 *   getHallFloorPlanDocuments     fdsvector'de Salon Planı dokümanı var mı (#164)
 *
 * ⚠️ Salon bilgileri ve hizmetler FUAR ALANINDAKİ salona aittir (V53): burada değiştirmek, o fuar
 *    alanındaki HER fuarı etkiler. Alan Planı durumu ve aktiflik yalnızca bu fuarındır.
 *
 * Backlog #204 (2026-09-26): "Alan Planına Git" artık SalonAlanPlaniPage'i açıyor
 * (/Management/Salonlar/:salonId/AlanPlani) — Salonlar listesindeki aynı eylemle tek yer. WAS: plan
 * bu kartın altında satır içi gömülüyordu; o blok yorum satırı olarak duruyor.
 *
 * Backlog #236 (2026-09-27): Alan Planı, salonun Salon Planı TAMAMLANDI olmadan başlatılamaz
 * (backend #235 aynı kuralla 409 döner; fuar alanında kayıtlı olmayan salon da kilitli). O sürece
 * Alan Planı rozetinde Çiziliyor / Tamamlandı, "Stand Çizimi Tamamlandı" ve "Alan Planına Git"
 * kilitli; nedeni satırda yazar. Çizim yok'a dönmek serbest.
 *
 * Backlog #291 (2026-10-08): hizmet rozetleri menü açıyor (HizmetDurumuPill) — WAS: her tıklama
 * sıradaki duruma geçiyordu. Seçim taslağa yazılır, kartın Kaydet'i gönderir.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
// WAS: ..., Alert, UncontrolledTooltip — tooltip yalnızca eski hizmet rozetlerindeydi; #291'de
// HizmetDurumuPill kendi tooltip'ini taşıyor.
import {
  Container, Row, Col, Card, CardHeader, CardBody, Table, Input, InputGroup, InputGroupText,
  Button, Badge, Spinner, Alert,
} from "reactstrap";
import { ArrowLeft, Pencil, Map as MapIcon, Palette, CircleCheck, Ruler } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
// #204: yalnızca yorum satırına alınan satır içi Alan Planı kullanıyordu.
// import KrokiVectorEmbed from "../../../Components/Kroki/KrokiVectorEmbed";
import { TEKNIK_ICONS } from "../../../Components/Projects/StandPills";
import { getFuarHMax } from "../../../services/fuarKuralService";
import {
  getFuarAlani, updateSalon, listSalonHizmetTipleri, addSalonToFuar, removeSalonFromFuar,
  setSalonPlaniDurumu, setAltyapiPlaniDurumu, setAlanPlaniDurumu,
} from "../../../services/fuarAlaniSalonService";
import { getSalonlarByFuar } from "../../../services/krokiService";
// WAS: KROKI_LAYERS, KROKI_MODES, getHallFloorPlanDocuments, resolveStandPlanDocument — son ikisi
// yalnızca yorum satırına alınan satır içi Alan Planı içindi (#204).
import {
  KROKI_LAYERS, getHallFloorPlanDocuments,
} from "../../../services/krokiVectorService";
import { isStaffAccount } from "../../../services/firmaSessionService";
// WAS (#194): hizmetDurumMeta, nextHizmetDurum — yalnızca tıklama-döngülü hizmet rozetleri
// kullanıyordu; #291'de HizmetDurumuPill'e geçti.
// import {
//   CIZIM_DURUM, CizimDurumuPill, hizmetDurumMeta, hizmetDurumOf, nextHizmetDurum, HIZMET_LEGEND,
//   alanPlaniEngeli, ALAN_PLANI_KILITLI_DURUMLAR,
// } from "../../../Components/FairAreas/salonDurumlari";
import {
  CIZIM_DURUM, CizimDurumuPill, hizmetDurumOf, HIZMET_LEGEND, HizmetDurumuPill,
  alanPlaniEngeli, ALAN_PLANI_KILITLI_DURUMLAR,
} from "../../../Components/FairAreas/salonDurumlari";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const asInput = (v) => (v == null ? "" : String(v));
const fmtM = (v) => (v == null ? "—" : `${Number(v).toFixed(2).replace(".", ",")} m`);
const fmtTarih = (v) => (v ? new Date(v).toLocaleString(currentLocaleTag()) : null);

function heightProblem(ceiling, allowed) {
  if (ceiling === "" || allowed === "") return null;
  return Number(allowed) > Number(ceiling) ? i18n.t("yonetim:salonKarti.izinVerilenYukseklikFizikselTavandan") : null;
}

export default function SalonKartiPage() {
  const { t: tx } = useTranslation("yonetim");
  const { salonId } = useParams();
  const { selectedFuarId } = useContext(MainContext);
  const personel = isStaffAccount();

  const [fuarCtx, setFuarCtx] = useState(null);   // getFuarHMax
  const [venueHall, setVenueHall] = useState(null); // getFuarAlani → halls[i]
  const [hizmetTipleri, setHizmetTipleri] = useState([]);
  const [standCount, setStandCount] = useState(null);
  const [salonPlaniDoc, setSalonPlaniDoc] = useState(undefined); // undefined = bilinmiyor, null = yok
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // #204: satır içi Alan Planı kaldırıldı (yorum satırı) — SalonAlanPlaniPage'e taşındı.
  // const [alanPlaniAcik, setAlanPlaniAcik] = useState(false);
  // const [alanPlaniProbe, setAlanPlaniProbe] = useState(null);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  useEffect(() => {
    let ignore = false;
    listSalonHizmetTipleri().then((rows) => { if (!ignore) setHizmetTipleri(rows); }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!selectedFuarId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const ctx = await getFuarHMax(selectedFuarId, { forceLive: true });
        if (ignore) return;
        setFuarCtx(ctx);
        const alaniId = ctx?.fuarAlaniId ?? null;
        const [alani, standInfo, docs] = await Promise.all([
          alaniId != null ? getFuarAlani(alaniId) : Promise.resolve(null),
          getSalonlarByFuar(selectedFuarId).catch(() => ({ salonlar: [] })),
          alaniId != null ? getHallFloorPlanDocuments(alaniId) : Promise.resolve({ ok: false }),
        ]);
        if (ignore) return;
        const hall = (alani?.halls ?? []).find((h) => String(h.salonId) === String(salonId)) ?? null;
        setVenueHall(hall);
        setDraft(hall ? {
          salonName: hall.salonName ?? "",
          ceilingHeight: asInput(hall.ceilingHeight),
          allowedHMax: asInput(hall.allowedHMax),
          brutM2: asInput(hall.brutM2),
          hizmetler: Object.fromEntries((hall.hizmetler || []).map((x) => [x.kod, hizmetDurumOf(x)])),
        } : null);
        const sc = (standInfo?.salonlar ?? []).find((s) => String(s.salonId) === String(salonId));
        setStandCount(sc ? sc.standCount : 0);
        setSalonPlaniDoc(docs.ok ? (docs.byHallId.get(String(salonId)) ?? null) : undefined);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [selectedFuarId, salonId, reloadToken]);

  const fairHall = useMemo(
    () => (fuarCtx?.halls ?? []).find((h) => String(h.salonId) === String(salonId)) ?? null,
    [fuarCtx, salonId],
  );
  const aktif = fairHall != null;
  // #236: null = Alan Planı başlatılabilir; yoksa nedeni.
  const alanEngel = aktif ? alanPlaniEngeli(fairHall) : null;
  const fuarAlaniId = fuarCtx?.fuarAlaniId ?? null;
  const salonAdi = venueHall?.salonName ?? fairHall?.salonName ?? `#${salonId}`;

  // #204: satır içi Alan Planı'nın yoklaması — SalonAlanPlaniPage aynı yoklamayı kendisi yapıyor.
  // // Alan Planı yalnızca fuara bağlı salonda var; gömmeden önce fdsvector'de yokla (KonumTab deseni).
  // useEffect(() => {
  //   if (!alanPlaniAcik || !selectedFuarId) return undefined;
  //   let ignore = false;
  //   resolveStandPlanDocument({ fairId: selectedFuarId, hallId: salonId })
  //     .then((r) => { if (!ignore) setAlanPlaniProbe(r); });
  //   return () => { ignore = true; };
  // }, [alanPlaniAcik, selectedFuarId, salonId]);

  async function run(key, fn, okMsg) {
    setBusy(key);
    setError(null);
    setConflicts(null);
    try {
      const out = await fn();
      setSuccessMsg(out?.message || okMsg);
      refresh();
      return true;
    } catch (e) {
      setError(e.message);
      if (e.conflicts) setConflicts(e.conflicts);
      return false;
    } finally {
      setBusy(null);
    }
  }

  // ── Salon bilgileri + hizmetler (fuar alanındaki salon) ──
  const patch = useMemo(() => {
    if (!venueHall || !draft) return {};
    const p = {};
    if (draft.salonName.trim() !== (venueHall.salonName ?? "")) p.salonName = draft.salonName;
    if (draft.ceilingHeight !== asInput(venueHall.ceilingHeight)) p.ceilingHeight = draft.ceilingHeight;
    if (draft.allowedHMax !== asInput(venueHall.allowedHMax)) p.allowedHMax = draft.allowedHMax;
    if (draft.brutM2 !== asInput(venueHall.brutM2)) p.brutM2 = draft.brutM2;
    const original = Object.fromEntries((venueHall.hizmetler || []).map((x) => [x.kod, hizmetDurumOf(x)]));
    const hp = {};
    Object.entries(draft.hizmetler || {}).forEach(([kod, d]) => { if (d !== (original[kod] ?? null)) hp[kod] = d; });
    if (Object.keys(hp).length > 0) p.hizmetler = hp;
    return p;
  }, [venueHall, draft]);
  const dirty = Object.keys(patch).length > 0;
  const problem = draft ? heightProblem(draft.ceilingHeight, draft.allowedHMax) : null;

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  // WAS (#194, #291'de menüye geçti):
  // const toggleHizmet = (kod) => setDraft((d) => ({
  //   ...d, hizmetler: { ...d.hizmetler, [kod]: nextHizmetDurum(d.hizmetler?.[kod] ?? null) },
  // }));
  // #291: menüden seçilen durum taslağa yazılır; kartın Kaydet'i gönderir.
  const setHizmet = (kod, durum) => setDraft((d) => ({
    ...d, hizmetler: { ...d.hizmetler, [kod]: durum },
  }));

  const handleSave = () => run("bilgi", () => updateSalon(fuarAlaniId, salonId, patch), "Salon kaydedildi.");

  // ── Çizim durumları ──
  const setDurum = (plan, durum) => {
    if (plan === "alanPlani") return run(plan, () => setAlanPlaniDurumu(selectedFuarId, salonId, durum), tx("salonKarti.alanPlaniDurumuKaydedildi"));
    const fn = plan === "altyapiPlani" ? setAltyapiPlaniDurumu : setSalonPlaniDurumu;
    return run(plan, () => fn(fuarAlaniId, salonId, durum), tx("salonKarti.cizimDurumuKaydedildi"));
  };

  const handleStandCizimiTamamlandi = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(tx("salonKarti.salonIcinBuFuarinStand", { salonAdi }))) return;
    setDurum("alanPlani", CIZIM_DURUM.TAMAMLANDI);
  };

  const handleAktiflik = () => (aktif
    ? run("aktif", () => removeSalonFromFuar(selectedFuarId, salonId), tx("salonKarti.buFuardanCikarildi", { salonAdi }))
    : run("aktif", () => addSalonToFuar(selectedFuarId, salonId), tx("salonKarti.buFuaraBaglandi", { salonAdi })));

  const planSayfasi = (katman) => (fuarAlaniId != null
    ? `/Management/FairAreas/${fuarAlaniId}/Salonlar/${salonId}/Plan${katman ? `?katman=${katman}` : ""}`
    : null);

  if (!selectedFuarId) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={tx("salonKarti.salonKarti")} pageTitle={tx("salonKarti.salonlarVeKrokiler")} />
          <Alert color="warning">{tx("salonKarti.onceUsttenBirFuarSecin")}</Alert>
        </Container>
      </div>
    );
  }

  const alanTarih = fmtTarih(fairHall?.alanPlaniDurumTarihi);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("salonKarti.salonKarti")} pageTitle={tx("salonKarti.salonlarVeKrokiler")} />

        {error && (
          <Alert color="danger" toggle={() => { setError(null); setConflicts(null); }}>
            {error}
            {conflicts?.length > 0 && (
              <ul className="mb-0 mt-2 small">
                {conflicts.map((c, i) => (
                  <li key={c.salonToFuarId ?? c.fuarId ?? i}>
                    {c.fuarName ?? (c.fuarId != null ? tx("salonKarti.fuar", { fuarId: c.fuarId }) : c.salonName ?? tx("salonKarti.kayit"))}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
        {successMsg && <Alert color="success">{successMsg}</Alert>}

        {/* ── Başlık ── */}
        <Card className="mb-3">
          <CardBody className="py-2 d-flex align-items-center gap-3 flex-wrap">
            <Link to="/Management/Salonlar" className="btn btn-light btn-sm">
              <ArrowLeft size={14} className="me-1" />{tx("salonKarti.salonlar")}
            </Link>
            <div className="flex-grow-1">
              <h5 className="mb-0">
                {tx("salonKarti.salon", { salonAdi })}
                {loading ? <Spinner size="sm" className="ms-2" /> : (
                  <Badge color={aktif ? "success" : "secondary"} pill className="ms-2 align-middle">
                    {aktif ? tx("salonKarti.buFuardaAktif") : tx("salonKarti.buFuardaPasif")}
                  </Badge>
                )}
              </h5>
              <div className="text-muted small">{fuarCtx?.fuarAlaniName ?? tx("salonKarti.fuarAlani")}{" "}{tx("salonKarti.fuar2")}{selectedFuarId}</div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Badge color={standCount > 0 ? "info" : "light"} className={standCount > 0 ? "" : "text-muted border"} pill>
                {standCount ?? "—"}{" "}{tx("salonKarti.stand")}
              </Badge>
              {aktif && (
                <Link to={`/Management/Salonlar/${salonId}/DurumHaritasi`} className="btn btn-soft-primary btn-sm">
                  <Palette size={14} className="me-1" />{tx("salonKarti.durumHaritasi")}
                </Link>
              )}
              <Link to="/Management/Fairs/Rules?tab=hmax" className="btn btn-soft-secondary btn-sm">
                <Ruler size={14} className="me-1" />{tx("salonKarti.yukseklikLimitleri")}
              </Link>
              {personel && !loading && venueHall && (
                <Button
                  size="sm" color={aktif ? "danger" : "primary"} outline
                  disabled={Boolean(busy) || (aktif && standCount > 0)}
                  title={aktif && standCount > 0 ? tx("salonKarti.buSalondaBuFuarIcin") : undefined}
                  onClick={handleAktiflik}
                >
                  {busy === "aktif" ? <Spinner size="sm" /> : aktif ? tx("salonKarti.pasifYap") : tx("salonKarti.aktifEt")}
                </Button>
              )}
            </div>
          </CardBody>
        </Card>

        {!loading && !venueHall && fuarCtx && (
          <Alert color="warning">
            {tx("salonKarti.salonBuFuarinFuarAlaninda", { salonId })}{fuarCtx.fuarAlaniName ?? "—"}{tx("salonKarti.kayitliDegil")}
            {fuarAlaniId != null && <> <Link to={`/Management/FairAreas/${fuarAlaniId}/Salonlar`}>{tx("salonKarti.fuarAlanlariSalonlar")}</Link></>}
          </Alert>
        )}

        {venueHall && draft && (
          <Row className="g-3">
            {/* ── Salon bilgileri + hizmetler ── */}
            <Col xl={6}>
              <Card className="h-100">
                <CardHeader className="d-flex align-items-center gap-2">
                  <h6 className="card-title mb-0 flex-grow-1">{tx("salonKarti.salonBilgileri")}</h6>
                  <span className="text-muted small">{tx("salonKarti.fuarAlanindakiTumFuarlarIcin")}</span>
                </CardHeader>
                <CardBody>
                  <Row className="g-3">
                    <Col md={6}>
                      <label className="form-label text-muted small mb-1">{tx("salonKarti.salonAdi")}</label>
                      <Input
                        bsSize="sm" value={draft.salonName}
                        disabled={!personel || venueHall.sharedWithOtherFairgrounds}
                        title={venueHall.sharedWithOtherFairgrounds ? tx("salonKarti.buSalonKaydiBaskaBir") : undefined}
                        onChange={(e) => setField("salonName", e.target.value)}
                      />
                      {venueHall.sharedWithOtherFairgrounds && (
                        <Badge color="light" className="text-muted border mt-1">{tx("salonKarti.baskaFuarAlaniylaPaylasiliyor")}</Badge>
                      )}
                    </Col>
                    <Col md={6}>
                      <label className="form-label text-muted small mb-1">{tx("salonKarti.brutM")}</label>
                      <InputGroup size="sm">
                        <Input type="number" min="0" step="0.1" placeholder={tx("salonKarti.kayitYok")} disabled={!personel}
                          value={draft.brutM2} onChange={(e) => setField("brutM2", e.target.value)} />
                        <InputGroupText>m²</InputGroupText>
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <label className="form-label text-muted small mb-1">{tx("salonKarti.fizikselTavan")}</label>
                      <InputGroup size="sm">
                        <Input type="number" min="0" step="0.1" placeholder={tx("salonKarti.kayitYok")} disabled={!personel}
                          value={draft.ceilingHeight} onChange={(e) => setField("ceilingHeight", e.target.value)} />
                        <InputGroupText>m</InputGroupText>
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <label className="form-label text-muted small mb-1">{tx("salonKarti.izinVerilenYukseklikVarsayilan")}</label>
                      <InputGroup size="sm">
                        <Input type="number" min="0" step="0.1" placeholder={tx("salonKarti.kayitYok")} disabled={!personel}
                          invalid={Boolean(problem)}
                          value={draft.allowedHMax} onChange={(e) => setField("allowedHMax", e.target.value)} />
                        <InputGroupText>m</InputGroupText>
                      </InputGroup>
                      {problem && <div className="text-danger small">{problem}</div>}
                    </Col>
                    <Col xs={12}>
                      <label className="form-label text-muted small mb-1 d-flex align-items-center gap-2 flex-wrap">
                        {tx("salonKarti.hizmetler")}
                        {HIZMET_LEGEND.map((l) => (
                          <Badge key={l.label} color={l.color} pill className={l.className} style={{ fontSize: 10 }}>{l.label}</Badge>
                        ))}
                      </label>
                      <div className="d-flex flex-wrap gap-2">
                        {/* WAS (#194, tıklama döngüsü — #291'de menüye geçti):
                        {hizmetTipleri.map((t) => {
                          const durum = draft.hizmetler?.[t.kod] ?? null;
                          const meta = hizmetDurumMeta(durum);
                          const Icon = TEKNIK_ICONS[t.kod.toLowerCase()];
                          const id = `kart-hizmet-${t.kod}`;
                          return (
                            <React.Fragment key={t.kod}>
                              <Badge
                                id={id} color={meta.color} pill
                                className={`d-inline-flex align-items-center gap-1 px-2 py-1 ${meta.className}`}
                                style={{ cursor: personel ? "pointer" : "default", fontSize: 12 }}
                                onClick={() => personel && toggleHizmet(t.kod)}
                              >
                                {Icon && <Icon size={12} />}{t.ad}: {meta.label}
                              </Badge>
                              <UncontrolledTooltip target={id} placement="top">
                                Tıklayın: Bilinmiyor → Var → Yok → Harici Destek
                              </UncontrolledTooltip>
                            </React.Fragment>
                          );
                        })}
                        */}
                        {/* #291: tıklayınca dört seçenekli menü (yalnızca personel); seçim taslağa yazılır. */}
                        {hizmetTipleri.map((t) => (
                          <HizmetDurumuPill
                            key={t.kod}
                            id={`kart-hizmet-${t.kod}`}
                            ad={t.ad}
                            Icon={TEKNIK_ICONS[t.kod.toLowerCase()]}
                            iconSize={12}
                            showLabel
                            className="px-2 py-1"
                            style={{ fontSize: 12 }}
                            durum={draft.hizmetler?.[t.kod] ?? null}
                            disabled={!personel}
                            onChange={(durum) => setHizmet(t.kod, durum)}
                          />
                        ))}
                      </div>
                    </Col>
                  </Row>
                  {personel && (
                    <div className="d-flex justify-content-end mt-3">
                      <Button size="sm" color="primary" disabled={!dirty || Boolean(problem) || !draft.salonName.trim() || Boolean(busy)} onClick={handleSave}>
                        {busy === "bilgi" ? <Spinner size="sm" /> : <><i className="bx bx-save me-1" />{tx("common:kaydet")}</>}
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            {/* ── Planlar ── */}
            <Col xl={6}>
              <Card className="h-100">
                <CardHeader><h6 className="card-title mb-0">{tx("salonKarti.planlarVeCizimDurumu")}</h6></CardHeader>
                <CardBody className="p-0">
                  <Table className="align-middle mb-0">
                    <tbody>
                      <tr>
                        <td className="ps-3">
                          <div className="fw-semibold">{tx("salonKarti.salonPlani")}</div>
                          <div className="text-muted small">
                            {tx("salonKarti.salonDuvarlariFuarAlanindakiTum")}
                            {salonPlaniDoc === undefined ? null : salonPlaniDoc
                              ? <Badge color="light" className="text-muted border">{tx("salonKarti.fdsvectorDokumaniVar")}</Badge>
                              : <Badge color="light" className="text-muted border">{tx("salonKarti.fdsvectorDeHenuzDokumanYok")}</Badge>}
                          </div>
                        </td>
                        <td>
                          <CizimDurumuPill id="kart-salon-plani" plan="salonPlani"
                            durum={venueHall.salonPlaniDurum ?? 0} tarih={venueHall.salonPlaniDurumTarihi}
                            busy={busy === "salonPlani"} disabled={!personel || Boolean(busy)}
                            onChange={(d) => setDurum("salonPlani", d)} />
                        </td>
                        <td className="text-end pe-3">
                          {personel && planSayfasi() && (
                            <Link to={planSayfasi(KROKI_LAYERS.FLOORPLAN)} className="btn btn-soft-warning btn-sm">
                              <Pencil size={13} className="me-1" />{tx("common:duzenle")}
                            </Link>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="ps-3">
                          <div className="fw-semibold">{tx("salonKarti.altyapiPlani")}</div>
                          <div className="text-muted small">{tx("salonKarti.elektrikSuHavaHatlariSalon")}</div>
                        </td>
                        <td>
                          <CizimDurumuPill id="kart-altyapi-plani" plan="altyapiPlani"
                            durum={venueHall.altyapiPlaniDurum ?? 0} tarih={venueHall.altyapiPlaniDurumTarihi}
                            busy={busy === "altyapiPlani"} disabled={!personel || Boolean(busy)}
                            onChange={(d) => setDurum("altyapiPlani", d)} />
                        </td>
                        <td className="text-end pe-3">
                          {personel && planSayfasi() && (
                            <Link to={planSayfasi(KROKI_LAYERS.INFRASTRUCTURE)} className="btn btn-soft-warning btn-sm">
                              <Pencil size={13} className="me-1" />{tx("common:duzenle")}
                            </Link>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="ps-3">
                          <div className="fw-semibold">{tx("salonKarti.alanPlaniKroki")}</div>
                          <div className="text-muted small">
                            {tx("salonKarti.buFuarinStandYerlesimi")}
                            {fairHall?.alanPlaniDurum === CIZIM_DURUM.TAMAMLANDI && alanTarih && (
                              <> — <strong>{tx("salonKarti.standCizimiTamamlandi")}{" "}{alanTarih}</strong></>
                            )}
                          </div>
                          {alanEngel && <div className="small text-warning mt-1">{alanEngel}</div>}
                        </td>
                        <td>
                          {aktif ? (
                            <CizimDurumuPill id="kart-alan-plani" plan="alanPlani"
                              durum={fairHall.alanPlaniDurum ?? 0} tarih={fairHall.alanPlaniDurumTarihi}
                              busy={busy === "alanPlani"} disabled={!personel || Boolean(busy)}
                              lockedDurums={alanEngel ? ALAN_PLANI_KILITLI_DURUMLAR : null} lockedReason={alanEngel}
                              onChange={(d) => setDurum("alanPlani", d)} />
                          ) : <span className="text-muted small">{tx("salonKarti.salonBuFuardaAktifDegil")}</span>}
                        </td>
                        <td className="text-end pe-3">
                          {aktif && personel && (
                            <div className="d-flex gap-1 justify-content-end flex-wrap">
                              {fairHall.alanPlaniDurum !== CIZIM_DURUM.TAMAMLANDI && (
                                // #236: devre dışı düğme title göstermez — nedeni saran span'da.
                                <span title={alanEngel ?? undefined}>
                                  <Button size="sm" color="success" outline disabled={Boolean(busy) || Boolean(alanEngel)} onClick={handleStandCizimiTamamlandi}>
                                    <CircleCheck size={13} className="me-1" />{tx("salonKarti.standCizimiTamamlandi2")}
                                  </Button>
                                </span>
                              )}
                              {/* WAS: <Button onClick={() => setAlanPlaniAcik((a) => !a)}>Alan Planına Git / Planı Kapat</Button> */}
                              {/* WAS (until #653): a DISABLED button (reason in a tooltip) while the Salon Planı was unfinished -
                                  {alanEngel ? (<span title={alanEngel}><span className="btn btn-soft-info btn-sm border disabled" aria-disabled="true">…</span></span>) : (<Link …/>)}
                                  Now always a link: the page it opens shows the reason itself, with the plan read-only. */}
                              <Link to={`/Management/Salonlar/${salonId}/AlanPlani`} className="btn btn-soft-info btn-sm border" title={alanEngel ?? undefined}>
                                <MapIcon size={13} className="me-1" />{tx("salonKarti.alanPlaninaGit")}
                              </Link>
                            </div>
                          )}
                        </td>
                      </tr>
                      {fairHall && (
                        <tr>
                          <td className="ps-3">
                            <div className="fw-semibold">{tx("salonKarti.yukseklikLimitleriBuFuar")}</div>
                            <div className="text-muted small">{tx("salonKarti.tavanVarsayilan", { fmtM: fmtM(venueHall.ceilingHeight) })}{" "}{fmtM(venueHall.allowedHMax)}</div>
                          </td>
                          <td colSpan={2} className="pe-3 small">
                            {tx("salonKarti.tekKatCiftKat", { fmtM: fmtM(fairHall.salonHMaxTekKat), fmtM2: fmtM(fairHall.salonHMaxCiftKat) })}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </Col>

            {/* ── Alan Planı (düzenleme, Alan Planı katmanında) ──
                #204: SalonAlanPlaniPage'e taşındı; aşağıdaki blok yorum satırı.
            {alanPlaniAcik && aktif && (
              <Col xs={12}>
                <Card>
                  <CardHeader><h6 className="card-title mb-0">Alan Planı — Salon {salonAdi}</h6></CardHeader>
                  <CardBody>
                    {!alanPlaniProbe ? (
                      <div className="text-center py-4"><Spinner color="primary" /></div>
                    ) : alanPlaniProbe.reason === "not-mapped" ? (
                      <Alert color="warning" className="mb-0">
                        Bu salonun bu fuar için planı fdsvector'de henüz yok — salon fdsvector'de bu fuara
                        etkinleştirilmemiş (backlog #165: FDS etkinleştirmeyi fdsvector'e iletiyor).
                      </Alert>
                    ) : (
                      // stand-plan dokümanı yoksa OLUŞTURMAZ (hallDocumentMap'ten çözer) — autoOpen'a gerek yok.
                      <KrokiVectorEmbed
                        fairId={selectedFuarId}
                        hallId={salonId}
                        hallName={salonAdi}
                        defaultMode={personel ? KROKI_MODES.EDIT : KROKI_MODES.VIEW}
                        allowEdit={personel}
                        activeLayer={KROKI_LAYERS.STANDS}
                        height={620}
                      />
                    )}
                  </CardBody>
                </Card>
              </Col>
            )}
            */}
          </Row>
        )}
      </Container>
    </div>
  );
}
